/**
 * MUTAGIM.SHOP — Homepage Categories Component
 * Affiche les catégories en vedette sur la page d'accueil
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function HomepageCategories() {
  const { i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const lang = i18n.language?.split('-')[0] || 'fr';

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories/homepage`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || []);
        }
      } catch (err) {
        console.error('Error fetching homepage categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <section className="w-full py-12 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
          </div>
        </div>
      </section>
    );
  }

  if (!categories.length) {
    return null;
  }

  return (
    <section className="w-full py-12 bg-zinc-950" data-testid="homepage-categories">
      <div className="container mx-auto px-4">
        {/* Section Title */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-white">
            {lang === 'fr' ? 'Nos Catégories' : lang === 'en' ? 'Our Categories' : 'הקטגוריות שלנו'}
          </h2>
          <Link 
            to="/categories" 
            className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
          >
            {lang === 'fr' ? 'Voir tout' : lang === 'en' ? 'View all' : 'צפה בהכל'}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Categories - Single horizontal scrollable row */}
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/category/${category.slug}`}
              className="group relative rounded-2xl overflow-hidden bg-zinc-900 hover:bg-zinc-800 transition-all duration-300 border border-zinc-800 hover:border-zinc-700 flex-shrink-0 w-[200px] md:w-[220px]"
              data-testid={`homepage-category-${category.slug}`}
            >
              {/* Category Image or Gradient Background */}
              <div className="aspect-[4/3] relative overflow-hidden">
                {category.image_url ? (
                  <img 
                    src={category.image_url}
                    alt={category.name?.[lang] || category.name?.fr}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : category.preview_products?.[0]?.image ? (
                  <img 
                    src={category.preview_products[0].image.startsWith('/api') 
                      ? `${API_URL}${category.preview_products[0].image}` 
                      : category.preview_products[0].image}
                    alt={category.name?.[lang] || category.name?.fr}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                    <span className="text-5xl">{category.emoji || '🏷️'}</span>
                  </div>
                )}
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                
                {/* Product count badge */}
                <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
                  <span className="text-xs text-white font-medium">
                    {category.products_count} {lang === 'fr' ? 'articles' : lang === 'en' ? 'items' : 'פריטים'}
                  </span>
                </div>
              </div>
              
              {/* Category Info */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{category.emoji || '🏷️'}</span>
                  <h3 className="text-lg font-semibold text-white group-hover:text-indigo-400 transition-colors">
                    {category.name?.[lang] || category.name?.fr || category.slug}
                  </h3>
                </div>
                {category.description?.[lang] && (
                  <p className="text-xs text-gray-400 line-clamp-1">
                    {category.description[lang]}
                  </p>
                )}
              </div>
              
              {/* Hover arrow */}
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
                  <ChevronRight className="w-5 h-5 text-white" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default HomepageCategories;
